'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Tabs, Tab, Select, SelectItem, Button } from '@heroui/react';
import { FilterSelect } from '../../../components/ui';
import { GlobalIc, ResetFiltersIc } from '../../../components/icons';
import { getProposalSources } from '../../../client/subscribers/Proposals/constants';
import { useNavigate } from 'react-router';
import { Typography, CustomTooltip } from '../../../components';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { useTranslations } from '../../../contexts/translation.context';
import CustomHeroButton from '../../../components/button';
import { EmptyState, MyOffersTable, OtherOffersTable } from './components';
import { MyOfferData, OtherOfferData, SalesFansFilters } from './types';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE } from './constants';
import { partnerOfferService } from '../../../api';
import { PartnerOffer } from '../../../api/partner/partnerOfferService';

interface SalesFansProps {}

const SalesFans: React.FC<SalesFansProps> = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // State for My Offers
  const [myOffersTab, setMyOffersTab] = useState('all');
  const [myOffersPage, setMyOffersPage] = useState(1);
  const [myOffersPageSize, setMyOffersPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [myOffersData, setMyOffersData] = useState<MyOfferData[]>([]);
  const [myOffersTotalCount, setMyOffersTotalCount] = useState(0);

  // State for Other Offers
  const [otherOffersPage, setOtherOffersPage] = useState(1);
  const [otherOffersPageSize, setOtherOffersPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [otherOffersData, setOtherOffersData] = useState<OtherOfferData[]>([]);
  const [otherOffersTotalCount, setOtherOffersTotalCount] = useState(0);
  const [otherOffersSource, setOtherOffersSource] = useState('all');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mapping functions to convert API data to component data
  const mapPartnerOfferToMyOffer = (offer: PartnerOffer): MyOfferData => {
    const minPrice =
      offer.subs.length > 0 ? Math.min(...offer.subs.map((sub) => parseFloat(sub.price))) : 0;
    const maxPrice =
      offer.subs.length > 0 ? Math.max(...offer.subs.map((sub) => parseFloat(sub.price))) : 0;
    const avgPrice =
      offer.subs.length > 0
        ? offer.subs.reduce((sum, sub) => sum + parseFloat(sub.price), 0) / offer.subs.length
        : 0;

    return {
      //@ts-ignore
      id: offer?.id,
      title: offer.description.substring(0, 50) + (offer.description.length > 50 ? '...' : ''),
      // Офферы, созданные до появления поля type, считаем CPL.
      offerType: offer.type === 'subscription' ? 'subscription' : 'cpl',
      status:
        offer.status === 'active'
          ? 'active'
          : offer.status === 'hidden'
            ? 'inactive'
            : offer.status === 'moderation'
              ? 'moderation'
              : offer.status === 'hidden'
                ? 'hidden'
                : 'moderation',
      price: {
        amount: avgPrice,
        currency: 'USD',
        pricePerFan: avgPrice,
      },
      fansRange: {
        min: offer.subs_min || 0,
        max: offer.subs_max || null,
        perDay: offer.subs_pre_day || 0,
      },
      orders: {
        active: offer.cpl_orders_count_by_status?.in_progress || 0,
        completed: offer.cpl_orders_count_by_status?.completed || 0,
        cancelled: offer.cpl_orders_count_by_status?.canceled || 0,
      },
      description: offer.description || '',
      of_models: offer.of_models?.length || null,
      tags: offer?.campaign_types,
      source: offer.source,
      requirements: offer.requirements || '',
      campaign_types: offer.campaign_types || [],
      other_source_name: offer.other_source_name || '',
      // owner данные теперь берутся из AuthContext в компоненте
      lastUpdated: offer.updated_at || offer.created_at,
      isMyOffer: true,
    };
  };

  const mapPartnerOfferToOtherOffer = (offer: PartnerOffer): OtherOfferData => {
    const minPrice =
      offer.subs.length > 0 ? Math.min(...offer.subs.map((sub) => parseFloat(sub.price))) : 0;
    const maxPrice =
      offer.subs.length > 0 ? Math.max(...offer.subs.map((sub) => parseFloat(sub.price))) : 0;
    const avgPrice =
      offer.subs.length > 0
        ? offer.subs.reduce((sum, sub) => sum + parseFloat(sub.price), 0) / offer.subs.length
        : 0;

    return {
      id: offer.id,
      name: `Offer ${offer.id}`,
      // Офферы, созданные до появления поля type, считаем CPL.
      offerType: offer.type === 'subscription' ? 'subscription' : 'cpl',
      status:
        offer.status === 'active'
          ? 'active'
          : offer.status === 'hidden'
            ? 'hidden'
            : offer.status === 'moderation'
              ? 'moderation'
              : offer.status === 'pending'
                ? 'pending'
                : offer.status === 'suspended'
                  ? 'suspended'
                  : 'inactive',
      price: {
        amount: avgPrice,
        currency: 'USD',
        pricePerFan: avgPrice,
      },
      owner: offer.owner,
      fansRange: {
        min: offer.subs_min || 0,
        max: offer.subs_max || null,
        perDay: offer.subs_pre_day || 0,
      },
      orders: {
        active: offer.active_campaigns || 0,
        completed: Math.floor((offer.active_campaigns || 0) * 0.8),
        cancelled: Math.floor((offer.active_campaigns || 0) * 0.1),
      },
      description: offer.description || '',
      tags: offer.campaign_types,
      of_models: offer.of_models?.length || null,
      lastUpdated: offer.updated_at || offer.created_at,
      //@ts-ignore
      other_source_name: offer.other_source_name || '',
      requirements: offer.requirements || '',
      type: 'marketer', // Default type
      source: offer.source,
      isMyOffer: false,
    };
  };

  const breadcrumbs = useMemo(
    () => [
      { label: t('salesFans.home'), href: '/' },
      { label: t('sidebar.subscribersSales'), isActive: true },
    ],
    [t]
  );

  usePageBreadcrumbs(breadcrumbs);

  // Fetch My Offers data
  const fetchMyOffers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const status =
        myOffersTab === 'all'
          ? null
          : myOffersTab === 'inactive'
            ? 'hidden'
            : (myOffersTab as 'active' | 'hidden' | 'moderation');

      const response = await partnerOfferService.getOffers({
        page: myOffersPage,
        page_size: myOffersPageSize,
        status: status,
      });

      setMyOffersData(response.objects.map(mapPartnerOfferToMyOffer));
      setMyOffersTotalCount(response.count);
    } catch (err) {
      console.error('Error fetching my offers:', err);
      setError(t('salesFans.errors.dataLoadError'));
      setMyOffersData([]);
      setMyOffersTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Other Offers data
  const fetchOtherOffers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await partnerOfferService.getOtherOffers({
        page: otherOffersPage,
        page_size: otherOffersPageSize,
        source:
          otherOffersSource !== 'all'
            ? (otherOffersSource as
                | 'only_fans'
                | 'reddit'
                | 'instagram'
                | 'tiktok'
                | 'google'
                | 'telegram'
                | 'twitter'
                | 'youtube'
                | 'another')
            : null,
      });

      setOtherOffersData(response.objects.map(mapPartnerOfferToOtherOffer));
      setOtherOffersTotalCount(response.count);
    } catch (err) {
      console.error('Error fetching other offers:', err);
      setError(t('salesFans.errors.dataLoadError'));
      setOtherOffersData([]);
      setOtherOffersTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMyOffers();
  }, [myOffersPage, myOffersPageSize, myOffersTab, t]);

  useEffect(() => {
    fetchOtherOffers();
  }, [otherOffersPage, otherOffersPageSize, otherOffersSource, t]);

  const handleMyOffersTabChange = (key: string) => {
    setMyOffersTab(key);
    setMyOffersPage(1);
  };

  const handleMyOffersPageChange = (page: number) => {
    setMyOffersPage(page);
  };

  const handleMyOffersPageSizeChange = (newPageSize: number) => {
    setMyOffersPageSize(newPageSize);
    setMyOffersPage(1);
  };

  const handleOtherOffersPageChange = (page: number) => {
    setOtherOffersPage(page);
  };

  const handleOtherOffersPageSizeChange = (newPageSize: number) => {
    setOtherOffersPageSize(newPageSize);
    setOtherOffersPage(1);
  };

  const handleOtherOffersSourceChange = (value: string) => {
    setOtherOffersSource(value);
    setOtherOffersPage(1);
  };

  const handleResetFilters = () => {
    setOtherOffersSource('all');
    setOtherOffersPage(1);
  };

  const handleEditOffer = (item: MyOfferData) => {
    console.log('Edit offer:', item);
  };

  const handleDeleteOffer = (item: MyOfferData) => {
    console.log('Delete offer:', item);
  };

  const handleOrderOffer = (item: OtherOfferData) => {
    console.log('Order offer:', item);
  };

  const handleCopyOffer = (item: OtherOfferData) => {
    console.log('Copy offer:', item);
  };

  const handleCreateOffer = () => {
    navigate('/partner/subscribers/sales/create-offer');
  };

  const renderOtherOffersEmptyState = () => (
    <EmptyState
      title={t('salesFans.noOffers')}
      description={t('salesFans.noOffersCategoryDescription')}
    />
  );

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-4 pt-6 md:px-6 md:py-[.6563rem] ">
        <div className="flex items-center justify-between">
          <Typography variant="h5">{t('sidebar.subscribersSales')}</Typography>
          <CustomHeroButton
            title={t('salesFans.addOffer')}
            className="font-semibold"
            onPress={handleCreateOffer}
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 md:px-6 md:py-4 flex-1 space-y-6 md:space-y-8">
        {/* Error Display */}
        {/* {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Ошибка загрузки данных
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* My Offers Section */}
        <div>
          <Typography variant="h6" className="font-medium text-gray-900 mb-6">
            {t('salesFans.sections.myOffers')}
          </Typography>

          {/* My Offers Tabs */}
          <div className="mb-4">
            <div className="bg-gray-100 rounded-xl w-fit">
              <Tabs
                selectedKey={myOffersTab}
                onSelectionChange={(key) => handleMyOffersTabChange(key as string)}
                variant="light"
                classNames={{
                  tabList: 'gap-1',
                  cursor: 'bg-white rounded-lg shadow-sm',
                  tab: 'px-2 md:px-3 py-1 text-sm font-normal data-[selected=true]:text-black text-gray-500',
                  tabContent: 'group-data-[selected=true]:text-black',
                }}
              >
                <Tab key="all" title={t('salesFans.all')} />
                <Tab key="active" title={t('salesFans.active')} />
                <Tab key="hidden" title={t('salesFans.inactive')} />
                <Tab key="moderation" title={t('salesFans.moderation')} />
              </Tabs>
            </div>
          </div>

          {/* My Offers Table */}
          <div className="h-full">
            <MyOffersTable
              data={myOffersData}
              isLoading={isLoading}
              totalCount={myOffersTotalCount}
              currentPage={myOffersPage}
              pageSize={myOffersPageSize}
              onPageChange={handleMyOffersPageChange}
              onPageSizeChange={handleMyOffersPageSizeChange}
              onEdit={handleEditOffer}
              onDelete={handleDeleteOffer}
            />
          </div>
        </div>

        {/* Other Offers Section */}
        <div>
          <div className="flex gap-6 flex-col mb-4">
            <Typography variant="h6" className="font-medium text-gray-900">
              {t('salesFans.sections.otherOffers')}
            </Typography>

            {/* Source Filter */}
            <div className="flex items-center gap-4">
              <div>
                <FilterSelect
                  placeholder={t('proposals.allSources')}
                  options={getProposalSources(t)}
                  selectedKey={otherOffersSource}
                  variant="campaign"
                  onSelectionChange={handleOtherOffersSourceChange}
                  // startContent={<GlobalIc/>}
                  autoWidth={true}
                />
              </div>
              {otherOffersSource !== 'all' && (
                <CustomTooltip content={t('partner.orders.filters.resetTooltip')} placement="top">
                  <Button
                    //@ts-ignore
                    size="xs"
                    className="h-9 rounded-2 !w-9 p-0 bg-auth-surface"
                    onPress={handleResetFilters}
                  >
                    <ResetFiltersIc />
                  </Button>
                </CustomTooltip>
              )}
            </div>
          </div>

          {/* Other Offers Table */}
          <div className="h-full">
            {otherOffersData.length === 0 && !isLoading ? (
              renderOtherOffersEmptyState()
            ) : (
              <OtherOffersTable
                data={otherOffersData}
                isLoading={isLoading}
                totalCount={otherOffersTotalCount}
                currentPage={otherOffersPage}
                pageSize={otherOffersPageSize}
                onPageChange={handleOtherOffersPageChange}
                onPageSizeChange={handleOtherOffersPageSizeChange}
                onOrder={handleOrderOffer}
                onCopyOffer={handleCopyOffer}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesFans;
